import targetingApi from 'domain/targeting/api'

export const actions = {
  setPostalCodes: 'setPostalCodes',
  setMatched: 'setMatched',
  setMatching: 'setMatchedLoading',
  resetPostalCodes: 'resetPostalCodes',
  reset: 'reset',
  clearForm: 'clearForm',

  matchAsync: 'matchAsync',
}

const _initialState = {
  countries: {},
  unmatchedPostalCodes: [],
  loading: false,
  success: null,
  serverError: false,
}

// A postal code's cross-platform identity is its number + country. `type` is
// always 'zip' and `country_code` is always known, so `name|zip|country_code`
// is a globally unique key. canonical_name is deliberately NOT part of it:
// Google returns one ("10001,New York,United States"), Meta never does, so
// keying on it splits a single physical zip matched on both platforms into
// two rows — the recurring double-count / double-display bug this replaces.
export function postalCodeKey({ name, country_code } = {}) {
  return `${name}|zip|${country_code ?? ''}`
}

// Normalizes one persisted per-platform postal-code wrapper into the single
// entry shape used throughout reducer state (the fresh zip-match path in
// matchAsync produces the same shape). Mirrors rangeLocationsReducer's
// dbLocationToLocation:
//   { name, canonical_name, type, country_code, key,
//     platform_results: { fb?, google? }, platforms: { fb, google } }
// `platform_results.<p>` keeps each platform's raw native payload (fb's
// opaque `key`, google's `geo_target_constant_id` / `resource_name`) verbatim
// for publish; `platforms` records which platform(s) this zip actually
// matched on, so a one-sided zip stays one-sided through a save round trip.
export function postalCodeToEntry(wrapper = {}, platform) {
  const identity = {
    name: wrapper.name,
    canonical_name: wrapper.canonical_name,
    type: wrapper.type ?? 'zip',
    country_code: wrapper.country_code,
  }
  return {
    ...identity,
    key: postalCodeKey(identity),
    platform_results: { [platform]: wrapper },
    platforms: { fb: platform === 'fb', google: platform === 'google' },
  }
}

// Merges targeting[].fb.postal_codes.countries and
// targeting[].google.postal_codes.countries into one unified per-TC list,
// deduped on postalCodeKey. A zip present under both platforms becomes one
// entry with both `platforms` flags true and a unioned `platform_results`;
// present under one becomes one entry with only that flag true.
export function mergePlatformCountries(fbCountries = {}, googleCountries = {}) {
  const merged = {}
  const countryKeys = new Set([...Object.keys(fbCountries), ...Object.keys(googleCountries)])
  for (const countryKey of countryKeys) {
    const byKey = new Map()
    const fold = (wrapper, platform) => {
      const entry = postalCodeToEntry(wrapper, platform)
      const existing = byKey.get(entry.key)
      if (!existing) {
        byKey.set(entry.key, entry)
        return
      }
      byKey.set(entry.key, {
        ...existing,
        canonical_name: existing.canonical_name ?? entry.canonical_name,
        platform_results: { ...existing.platform_results, ...entry.platform_results },
        platforms: {
          fb: existing.platforms.fb || entry.platforms.fb,
          google: existing.platforms.google || entry.platforms.google,
        },
      })
    }
    for (const wrapper of fbCountries[countryKey] ?? []) fold(wrapper, 'fb')
    for (const wrapper of googleCountries[countryKey] ?? []) fold(wrapper, 'google')
    merged[countryKey] = Array.from(byKey.values())
  }
  return merged
}

// Splits the unified list back into per-platform postal_codes.countries —
// only a zip tagged for `platform` and holding that platform's native
// payload is emitted (a zip matched on one platform can't be sent to the
// other). name/canonical_name/type/country_code ride along on each payload
// (fb's own raw match has no name) so postalCodeKey recomputes identically
// on reload — mirrors splitLocationsByPlatform in rangeLocationsReducer.
export function splitCountriesByPlatform(countries = {}) {
  const fb = {}
  const google = {}
  const append = (bucket, countryKey, value) => {
    bucket[countryKey] = [...(bucket[countryKey] ?? []), value]
  }
  for (const [countryKey, entries] of Object.entries(countries)) {
    for (const entry of entries) {
      const identity = {
        name: entry.name,
        type: entry.type ?? 'zip',
        country_code: entry.country_code,
      }
      const fbTagged = entry.platforms ? !!entry.platforms.fb : !!entry.platform_results?.fb
      const googleTagged = entry.platforms
        ? !!entry.platforms.google
        : !!entry.platform_results?.google

      if (fbTagged && entry.platform_results?.fb) {
        append(fb, countryKey, { ...entry.platform_results.fb, ...identity })
      }
      if (googleTagged && entry.platform_results?.google) {
        append(google, countryKey, {
          ...entry.platform_results.google,
          ...identity,
          canonical_name: entry.canonical_name ?? entry.platform_results.google.canonical_name,
        })
      }
      if (!entry.platform_results) {
        // Pre-migration entry with no recorded platform_results — assume fb
        // (the only platform this flow supported before cross-platform).
        append(fb, countryKey, entry)
      }
    }
  }
  return { fb, google }
}

export const initialState = (tcTargeting = {}) => {
  return {
    ..._initialState,
    countries: mergePlatformCountries(
      tcTargeting?.fb?.postal_codes?.countries,
      tcTargeting?.google?.postal_codes?.countries,
    ),
  }
}

export const reducer = (state, action) => {
  switch (action.type) {
    case actions.setPostalCodes:
      return { ...state, ...action.payload }
    case actions.resetPostalCodes:
      return {
        ...state,
        ...initialState(),
        ...(action.payload ?? {}),
      }
    case actions.reset: {
      const { targeting, countries } = action.payload

      let newState = {
        ..._initialState,
        countries: state.countries,
      }

      if (targeting) {
        newState = {
          ...newState,
          ...initialState(targeting),
        }
      } else {
        if (countries) {
          newState.countries = countries
        }
      }

      // delete 0 length countries
      Object.keys(newState.countries).forEach((key) => {
        if (!newState.countries[key].length) {
          delete newState.countries[key]
        }
      })

      return newState
    }
    case actions.setMatched: {
      const { matchedPostalCodes, unmatchedPostalCodes, serverError, success, countryKey } =
        action.payload
      const countries = {
        ...state.countries,
        ...(matchedPostalCodes.length ? { [countryKey]: matchedPostalCodes } : {}),
      }

      return {
        ...state,
        countries,
        unmatchedPostalCodes: unmatchedPostalCodes.map((z) => z.replace(/^.*:/, '')),
        success,
        serverError,
        loading: false,
      }
    }
    case actions.setMatching:
      return { ...state, loading: true }
    case actions.clearForm:
      return { ..._initialState, countries: state.countries }
    default:
      return state
  }
}

export const asyncActionHandlers = {
  [actions.matchAsync]:
    ({ dispatch }) =>
    async (action) => {
      const { countryKey, postalCodes, platforms = ['fb'] } = action.payload
      dispatch({ type: actions.setMatching })

      const { data, error } = await targetingApi.matchPostalCodesAsync(
        countryKey,
        postalCodes,
        platforms,
      )
      if (error) {
        console.error('Error matching customers', error)

        dispatch({
          type: actions.setMatched,
          payload: {
            success: false,
            matchedPostalCodes: [],
            unmatchedPostalCodes: [],
            serverError: true,
            countryKey,
          },
        })

        return
      }

      const { matched, unmatched } = data
      // Normalize fresh match results into the same entry shape a reload
      // produces (postalCodeToEntry) so state.countries holds exactly one
      // shape regardless of origin.
      const matchedZips = matched.map((z) => {
        const platformResults = z.platform_results ?? {}
        const identity = {
          name: z.zip,
          canonical_name: platformResults.google?.canonical_name,
          type: 'zip',
          country_code: countryKey,
        }
        return {
          ...identity,
          key: postalCodeKey(identity),
          platform_results: platformResults,
          platforms: { fb: !!platformResults.fb, google: !!platformResults.google },
        }
      })
      const payload = {
        success: (!unmatched || unmatched.length === 0) && !error,
        matchedPostalCodes: matchedZips,
        unmatchedPostalCodes: unmatched ?? [],
        serverError: false,
        countryKey,
      }

      dispatch({ type: actions.setMatched, payload })
    },
}

export function getCountryPostalCodes(countries, countryCode) {
  if (!countryCode) return []

  return countries[countryCode] ?? []
}

export function getCountryPostalCodeNames(countries, countryCode) {
  return getCountryPostalCodes(countries, countryCode).map((z) => z.name)
}

export function hasValidState(state) {
  return Object.values(state.countries).some((c) => c.length)
}

export function totalCountries(countries) {
  return Object.keys(countries).length
}
